import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Sparkles, Loader2, Inbox, KanbanSquare, Tag, UserPlus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useSalesOutlets } from '@/modules/sales-research/api';
import { usePipeline, useOutreachQueue, useRunDraftAgent, useAgentRuns } from './api';
import { PipelineBoard } from './PipelineBoard';
import { ApprovalQueue } from './ApprovalQueue';
import { OffersTemplates } from './OffersTemplates';
import { ContactsTab } from './ContactsTab';
import { CompanyDrawer } from './CompanyDrawer';
import { NewLeadModal } from './NewLeadModal';
import { Select } from './widgets';

type Tab = 'pipeline' | 'queue' | 'contacts' | 'assets';

export function CorporatePage() {
  const qc = useQueryClient();
  const outletsQ = useSalesOutlets();
  const pipelineQ = usePipeline();
  const queueQ = useOutreachQueue('pending');
  const runsQ = useAgentRuns();
  const runAgent = useRunDraftAgent();

  const [tab, setTab] = useState<Tab>('pipeline');
  const [city, setCity] = useState('');
  const [outletId, setOutletId] = useState('');
  const [step, setStep] = useState<'0' | '1'>('0');
  const [dryRun, setDryRun] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [newLead, setNewLead] = useState(false);

  const outlets = outletsQ.data ?? [];
  const cities = useMemo(() => Array.from(new Set(outlets.map((o) => o.city).filter(Boolean))) as string[], [outlets]);
  const visibleOutlets = useMemo(() => (city ? outlets.filter((o) => o.city === city) : outlets), [outlets, city]);

  const rows = useMemo(() => {
    let r = pipelineQ.data ?? [];
    if (city) r = r.filter((x) => x.primary_city === city);
    if (outletId) r = r.filter((x) => x.nearest_outlet_id === outletId);
    return r;
  }, [pipelineQ.data, city, outletId]);

  // Derive the open row from the live pipeline data so the drawer reflects
  // stage/contact changes as soon as the pipeline refetches (e.g. after enrich).
  const openRow = useMemo(
    () => (openId ? (pipelineQ.data ?? []).find((r) => r.id === openId) ?? null : null),
    [openId, pipelineQ.data],
  );
  const pendingCount = queueQ.data?.length ?? 0;
  const lastRun = runsQ.data?.[0];
  const managerNames = useMemo(
    () => Array.from(new Set((pipelineQ.data ?? []).map((r) => r.assigned_name).filter(Boolean))) as string[],
    [pipelineQ.data],
  );

  async function invalidate() {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['corp'] }),
      qc.invalidateQueries({ queryKey: ['sales', 'companies'] }),
    ]);
  }
  function runDraft() {
    const body: Record<string, unknown> = { dry_run: dryRun, sequence_step: Number(step), limit: 8 };
    if (outletId) body.outlet_id = outletId;
    else if (city) body.city = city;
    runAgent.mutate(body, { onSuccess: () => { invalidate(); if (!dryRun) setTab('queue'); } });
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Corporate</h1>
          <p className="text-sm text-muted-foreground">
            Pipeline, the AI draft agent, and a human approval queue. Nothing sends without your approval.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button size="sm" onClick={() => setNewLead(true)}>
              <UserPlus className="h-4 w-4" /> Add company
            </Button>
            <Select value={city} onChange={(v) => { setCity(v); setOutletId(''); }} placeholder="All cities"
              options={cities.map((c) => ({ value: c, label: c }))} />
            <Select value={outletId} onChange={setOutletId} placeholder="All outlets"
              options={visibleOutlets.map((o) => ({ value: o.outlet_id, label: o.name }))} />
            <Select value={step} onChange={(v) => setStep(v as '0' | '1')}
              options={[{ value: '0', label: 'Intro' }, { value: '1', label: 'Follow-ups' }]} />
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} /> Dry run
            </label>
            <Button size="sm" onClick={runDraft} disabled={runAgent.isPending}>
              {runAgent.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Run draft agent
            </Button>
          </div>
          {(runAgent.data || runAgent.error || lastRun) && (
            <span className="text-2xs text-muted-foreground">
              {runAgent.error
                ? <span className="text-[#B15599]">{String(runAgent.error)}</span>
                : runAgent.data
                ? `${runAgent.data.dry_run ? 'Dry run: ' : ''}${runAgent.data.created ?? 0} drafted of ${runAgent.data.considered ?? 0} considered${runAgent.data.use_claude ? '' : ' · template mode (no ANTHROPIC_API_KEY)'}`
                : lastRun
                ? `Last agent run: ${lastRun.drafts_created} drafts · ${new Date(lastRun.started_at).toLocaleString()}`
                : ''}
            </span>
          )}
        </div>
      </div>

      {/* tabs */}
      <div className="flex items-center gap-1 border-b border-border/60">
        <TabButton active={tab === 'pipeline'} onClick={() => setTab('pipeline')} icon={KanbanSquare}>Pipeline</TabButton>
        <TabButton active={tab === 'queue'} onClick={() => setTab('queue')} icon={Inbox}>
          Approval queue {pendingCount > 0 && <Badge variant="muted" className="ml-1">{pendingCount}</Badge>}
        </TabButton>
        <TabButton active={tab === 'contacts'} onClick={() => setTab('contacts')} icon={Users}>Contacts</TabButton>
        <TabButton active={tab === 'assets'} onClick={() => setTab('assets')} icon={Tag}>Offers &amp; templates</TabButton>
      </div>

      {tab === 'pipeline' && <PipelineBoard rows={rows} loading={pipelineQ.isLoading} onOpen={(r) => setOpenId(r.id)} />}
      {tab === 'queue' && <ApprovalQueue />}
      {tab === 'contacts' && <ContactsTab />}
      {tab === 'assets' && <OffersTemplates />}

      <CompanyDrawer row={openRow} managerNames={managerNames} onClose={() => setOpenId(null)} onChanged={invalidate} />
      <NewLeadModal open={newLead} managerNames={managerNames} onClose={() => { setNewLead(false); invalidate(); }} />
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, children }: {
  active: boolean; onClick: () => void; icon: typeof Inbox; children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
        active ? 'border-[#51BFAA] text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4" /> {children}
    </button>
  );
}
